import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import {
    addPdfPage,
    createPdfDocument,
    drawPdfPageChrome,
    getLensLogoDataUrl,
    getPdfContentInsets,
    getPdfContentWidthPt,
    PDF_PAGE_CHROME,
    ptToCssPx,
} from '../../utils/pdfPageChrome';

const BRAND_PRIMARY = '#003048';
const BODY_COLOR = '#003048';

const escapeHtml = text =>
    String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const escapeXml = text =>
    String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const sanitizeFilename = name => {
    const cleaned = (name || 'Untitled')
        .trim()
        .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
        .replace(/\s+/g, ' ')
        .slice(0, 80);
    return cleaned || 'Untitled';
};

const getNoteExportStyles = () => `
    body {
      margin: 0;
      padding: 0;
      font-family: Inter, Arial, Helvetica, sans-serif;
      color: ${BODY_COLOR};
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .note-export {
      width: 100%;
      max-width: 100%;
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    .note-export h1.doc-title {
      margin: 0 0 20px;
      font-size: 32px;
      font-weight: 800;
      line-height: 1.2;
      color: ${BRAND_PRIMARY};
      word-break: break-word;
    }
    .note-export .doc-body {
      font-size: 15px;
      font-weight: 400;
      line-height: 1.6;
      color: ${BODY_COLOR};
    }
    .note-export .doc-body p { margin: 0 0 10px; }
    .note-export .doc-body h1,
    .note-export .doc-body h2,
    .note-export .doc-body h3,
    .note-export .doc-body h4 {
      margin: 12px 0 8px;
      font-weight: 700;
      line-height: 1.4;
      color: ${BRAND_PRIMARY};
    }
    .note-export .doc-body h1 { font-size: 22px; }
    .note-export .doc-body h2 { font-size: 18px; }
    .note-export .doc-body h3,
    .note-export .doc-body h4 { font-size: 16px; }
    .note-export .doc-body ul,
    .note-export .doc-body ol {
      margin: 0 0 8px;
      padding-left: 1.4em;
    }
    .note-export .doc-body ul { list-style-type: disc; }
    .note-export .doc-body ol { list-style-type: decimal; }
    .note-export .doc-body ol.export-ordered-list {
      list-style: none;
      padding-left: 0;
    }
    .note-export .doc-body ol.export-ordered-list > li {
      margin-left: 0;
      padding-left: 0;
    }
    .note-export .doc-body .export-list-number {
      font-weight: 700;
      color: ${BRAND_PRIMARY};
    }
    .note-export .doc-body ol.export-ordered-list > li > ul {
      padding-left: 1.4em;
    }
    .note-export .doc-body li { margin: 0 0 6px; }
    .note-export .doc-body a { color: #c89741; }
    .note-export .doc-body img {
      max-width: 100%;
      height: auto;
      display: block;
    }
    .note-export .doc-body table.doc-table,
    .note-export .doc-body table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      table-layout: fixed;
      margin: 10px 0 14px;
      font-size: 12px;
      line-height: 1.45;
    }
    .note-export .doc-body table th,
    .note-export .doc-body table td {
      border: 1px solid #dfe2ec;
      border-right: 0;
      border-bottom: 0;
      padding: 6px 8px;
      text-align: left;
      vertical-align: top;
      word-break: break-word;
      box-sizing: border-box;
    }
    .note-export .doc-body table th:last-child,
    .note-export .doc-body table td:last-child {
      border-right: 1px solid #dfe2ec;
    }
    .note-export .doc-body table tr:last-child td {
      border-bottom: 1px solid #dfe2ec;
    }
    .note-export .doc-body table thead th {
      border-bottom: 1px solid #dfe2ec;
    }
    .note-export .doc-body table th {
      background: #f3f4f8;
      font-weight: 700;
      color: ${BRAND_PRIMARY};
    }
    .note-export .doc-body table tbody tr:nth-child(even) td {
      background: #fbfcfe;
    }
    .note-export .doc-body figure.doc-mermaid,
    .note-export .doc-body figure.doc-mermaid-fallback {
      margin: 12px 0 16px;
      padding: 0;
      text-align: center;
    }
    .note-export .doc-body figure.doc-mermaid img {
      margin: 0 auto;
      max-width: 100%;
      height: auto;
    }
    .note-export .doc-body figure.doc-mermaid-fallback figcaption {
      margin: 0 0 6px;
      font-size: 12px;
      font-weight: 700;
      color: #5c617a;
      text-align: left;
    }
    .note-export .doc-body pre.doc-code,
    .note-export .doc-body pre {
      margin: 10px 0 14px;
      padding: 10px 12px;
      background: #fafbfd;
      border: 1px solid #dfe2ec;
      border-radius: 6px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
      font-size: 12px;
      line-height: 1.5;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .note-export .doc-body blockquote {
      margin: 8px 0;
      padding-left: 12px;
      border-left: 3px solid #dddee4;
      color: #5c617a;
    }
    .note-export .doc-body strong,
    .note-export .doc-body b { font-weight: 700; }
    .note-export .doc-body em,
    .note-export .doc-body i { font-style: italic; }
    .note-export .doc-body u { text-decoration: underline; }
    .note-export .doc-body s,
    .note-export .doc-body strike { text-decoration: line-through; }
`;

/** Build a styled HTML document matching Journal editor typography. */
export const buildNoteExportHtml = ({ title, bodyHtml, includeTitle = true, contentWidthPx }) => {
    const safeTitle = escapeHtml(title || 'Untitled');
    const content = bodyHtml?.trim() || '<p></p>';
    const titleBlock = includeTitle ? `<h1 class="doc-title">${safeTitle}</h1>` : '';
    const widthStyle = contentWidthPx ? `width:${contentWidthPx}px;max-width:100%;` : 'max-width:100%;';

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${safeTitle}</title>
  <style>
    ${getNoteExportStyles()}
    .note-export {
      ${widthStyle}
    }
  </style>
</head>
<body>
  <article class="note-export">
    ${titleBlock}
    <div class="doc-body">${content}</div>
  </article>
</body>
</html>`;
};

const createOffscreenExportRoot = (html, contentWidthPx) => {
    const host = document.createElement('div');
    host.setAttribute('data-journal-export-root', 'true');
    host.style.cssText = `position:fixed;left:-10000px;top:0;width:${contentWidthPx}px;background:#fff;z-index:-1;overflow:visible;`;
    host.innerHTML = html;
    document.body.appendChild(host);
    return host;
};

const IMAGE_WAIT_MS = 8000;

const waitForImages = async (root, timeoutMs = IMAGE_WAIT_MS) => {
    const images = Array.from(root?.querySelectorAll?.('img') || []);
    if (!images.length) return;

    await Promise.all(
        images.map(
            img =>
                new Promise(resolve => {
                    const finish = () => resolve();
                    const timer = setTimeout(finish, timeoutMs);
                    const done = () => {
                        clearTimeout(timer);
                        finish();
                    };

                    if (img.complete) {
                        if (typeof img.decode === 'function') {
                            img.decode().then(done, done);
                        } else {
                            done();
                        }
                        return;
                    }

                    img.addEventListener('load', done, { once: true });
                    img.addEventListener('error', done, { once: true });
                }),
        ),
    );
};

const measureExportArticleHeight = async (articleHtml, contentWidthPx) => {
    const host = document.createElement('div');
    host.setAttribute('data-journal-export-measure', 'true');
    host.style.cssText = `position:fixed;left:-10000px;top:0;width:${contentWidthPx}px;background:#fff;visibility:hidden;pointer-events:none;overflow:visible;`;
    host.innerHTML = `<style>${getNoteExportStyles()}</style><article class="note-export">${articleHtml}</article>`;
    document.body.appendChild(host);
    await waitForImages(host);
    const height = host.scrollHeight;
    host.remove();
    return height;
};

const buildExportArticleHtml = ({ title, blocks, includeTitle }) => {
    const titleBlock = includeTitle
        ? `<h1 class="doc-title">${escapeHtml(title || 'Untitled')}</h1>`
        : '';
    return `${titleBlock}<div class="doc-body">${blocks.join('')}</div>`;
};

/** Remove a leading heading when it duplicates the document title. */
export const stripDuplicateLeadingHeading = (bodyHtml, title) => {
    if (!bodyHtml || !title) return bodyHtml;

    const normalizedTitle = title.trim().toLowerCase();
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="strip-root">${bodyHtml}</div>`, 'text/html');
    const root = doc.getElementById('strip-root');
    const first = root?.firstElementChild;
    if (!first) return bodyHtml;

    const tag = first.tagName?.toLowerCase();
    if (!['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) return bodyHtml;
    if (first.textContent?.trim().toLowerCase() !== normalizedTitle) return bodyHtml;

    first.remove();
    return root.innerHTML.trim() || '<p></p>';
};

/** Merge adjacent ordered lists and bake visible numbers for html2canvas PDF export. */
export const normalizeExportListHtml = bodyHtml => {
    if (!bodyHtml) return bodyHtml;

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="export-root">${bodyHtml}</div>`, 'text/html');
    const root = doc.getElementById('export-root');
    if (!root) return bodyHtml;

    const mergedNodes = [];
    let pendingOl = null;

    const flushPendingOl = () => {
        if (!pendingOl?.children.length) {
            pendingOl = null;
            return;
        }
        mergedNodes.push(pendingOl);
        pendingOl = null;
    };

    Array.from(root.childNodes).forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'ol') {
            if (!pendingOl) {
                pendingOl = doc.createElement('ol');
            }
            Array.from(node.children).forEach(child => {
                if (child.tagName?.toLowerCase() === 'li') {
                    pendingOl.appendChild(child.cloneNode(true));
                }
            });
            return;
        }

        flushPendingOl();
        mergedNodes.push(node.cloneNode(true));
    });
    flushPendingOl();

    root.innerHTML = '';
    mergedNodes.forEach(node => root.appendChild(node));

    root.querySelectorAll('ol').forEach(ol => {
        ol.classList.add('export-ordered-list');
        let index = parseInt(ol.getAttribute('start') || '1', 10);
        Array.from(ol.children).forEach(child => {
            if (child.tagName?.toLowerCase() !== 'li') return;
            if (child.querySelector(':scope > .export-list-number')) {
                index += 1;
                return;
            }

            const prefix = doc.createElement('span');
            prefix.className = 'export-list-number';
            prefix.textContent = `${index}. `;
            child.insertBefore(prefix, child.firstChild);
            index += 1;
        });
    });

    return root.innerHTML.trim() || '<p></p>';
};

/** Split body HTML into block-level chunks so page breaks stay between paragraphs/headings/list items. */
const splitBodyHtmlBlocks = bodyHtml => {
    const parser = new DOMParser();
    const wrapper = parser.parseFromString(`<div id="export-root">${bodyHtml || ''}</div>`, 'text/html');
    const root = wrapper.getElementById('export-root');
    const blocks = [];

    Array.from(root?.childNodes || []).forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent?.trim();
            if (text) blocks.push(`<p>${escapeHtml(text)}</p>`);
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();
        if (tag === 'ul' || tag === 'ol') {
            blocks.push(node.outerHTML);
            return;
        }

        blocks.push(node.outerHTML);
    });

    return blocks.length ? blocks : ['<p></p>'];
};

const paginateExportBlocks = async ({ blocks, title, includeTitle, maxContentHeightPx, contentWidthPx }) => {
    const pages = [];
    let currentBlocks = [];

    const measurePageBlocks = (pageBlocks, pageIncludeTitle) =>
        measureExportArticleHeight(
            buildExportArticleHtml({ title, blocks: pageBlocks, includeTitle: pageIncludeTitle }),
            contentWidthPx,
        );

    const flushCurrentPage = () => {
        if (!currentBlocks.length) return;
        pages.push({
            blocks: currentBlocks,
            includeTitle: pages.length === 0 && includeTitle,
        });
        currentBlocks = [];
    };

    for (const block of blocks) {
        const blockHeight = await measureExportArticleHeight(
            `<div class="doc-body">${block}</div>`,
            contentWidthPx,
        );
        const pageIncludeTitle = pages.length === 0 && currentBlocks.length === 0 && includeTitle;
        const nextHeight = await measurePageBlocks([...currentBlocks, block], pageIncludeTitle);

        if (blockHeight > maxContentHeightPx) {
            flushCurrentPage();
            pages.push({
                blocks: [block],
                includeTitle: pages.length === 0 && includeTitle,
                oversized: true,
            });
            continue;
        }

        if (nextHeight > maxContentHeightPx && currentBlocks.length > 0) {
            flushCurrentPage();
        }

        currentBlocks.push(block);
    }

    flushCurrentPage();
    return pages;
};

const MIN_PAGE_SLICE_PX = 32;
const PAGE_BREAK_SEARCH_PX = 120;
const PAGE_HEIGHT_SAFETY_RATIO = 0.97;

const downloadBlob = (blob, filename) => {
    if (typeof saveAs === 'function') {
        saveAs(blob, filename);
        return;
    }
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
};

const isCanvasRowBlank = (ctx, y, width, step = 4) => {
    const row = ctx.getImageData(0, y, width, 1).data;
    let blank = 0;
    let samples = 0;

    for (let x = 0; x < width; x += step) {
        samples += 1;
        const i = x * 4;
        const r = row[i];
        const g = row[i + 1];
        const b = row[i + 2];
        if (r >= 248 && g >= 248 && b >= 248) blank += 1;
    }

    return samples > 0 && blank / samples >= 0.97;
};

/** Pick a break point in blank space between paragraphs/lines. */
const findSafeCanvasBreakY = (canvas, startY, maxHeightPx) => {
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const safeMaxHeightPx = Math.floor(maxHeightPx * PAGE_HEIGHT_SAFETY_RATIO);
    const idealEndY = Math.min(height, startY + safeMaxHeightPx);

    if (idealEndY >= height - 1) return height;

    const hasBlankRun = y => {
        for (let offset = 0; offset < 4; offset += 1) {
            if (!isCanvasRowBlank(ctx, y - offset, width)) return false;
        }
        return true;
    };

    for (let y = idealEndY; y > idealEndY - PAGE_BREAK_SEARCH_PX && y > startY + MIN_PAGE_SLICE_PX; y -= 1) {
        if (hasBlankRun(y)) return y;
    }

    return idealEndY;
};

const renderCanvasSlice = (canvas, sourceY, sourceHeight) => {
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = Math.max(1, sourceHeight);
    const ctx = pageCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
    return pageCanvas.toDataURL('image/jpeg', 0.95);
};

const renderArticleToCanvas = async (article, contentWidthPx) =>
    html2canvas(article, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: contentWidthPx,
    });

const addCanvasToPdf = ({
    pdf,
    canvas,
    pageIndex,
    contentWidthPt,
    contentTop,
    contentHeight,
    marginX,
    logoDataUrl,
    allowSlice = false,
}) => {
    const imgWidth = contentWidthPt;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const needsSlice = allowSlice || imgHeight > contentHeight + 0.5;

    if (!needsSlice) {
        if (pageIndex > 0) addPdfPage(pdf);
        drawPdfPageChrome(pdf, logoDataUrl, { marginX });
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', marginX, contentTop, imgWidth, imgHeight);
        return pageIndex + 1;
    }

    const maxSlicePx = (contentHeight / imgHeight) * canvas.height;
    let sourceY = 0;
    let nextPageIndex = pageIndex;

    while (sourceY < canvas.height - 1) {
        const remainingPx = canvas.height - sourceY;
        const breakY =
            remainingPx <= maxSlicePx + 1 ? canvas.height : findSafeCanvasBreakY(canvas, sourceY, maxSlicePx);
        const sourceHeight = Math.max(1, breakY - sourceY);
        const sliceHeightPt = (sourceHeight / canvas.height) * imgHeight;
        const pageImg = renderCanvasSlice(canvas, sourceY, sourceHeight);

        if (nextPageIndex > 0) addPdfPage(pdf);
        drawPdfPageChrome(pdf, logoDataUrl, { marginX });
        pdf.addImage(pageImg, 'JPEG', marginX, contentTop, imgWidth, sliceHeightPt);

        sourceY = breakY;
        nextPageIndex += 1;
    }

    return nextPageIndex;
};

/** Export note as a multi-page PDF while preserving visual styling. */
export const exportNoteAsPdf = async ({ title, bodyHtml, includeTitle = true, filename: filenameOverride }) => {
    const filename =
        filenameOverride ||
        `${sanitizeFilename(includeTitle ? title || 'Untitled' : title || 'export')}.pdf`;

    const marginX = PDF_PAGE_CHROME.marginX;
    const pdf = createPdfDocument();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const { contentTop, contentBottom } = getPdfContentInsets({ marginX, doc: pdf });
    const contentWidthPt = getPdfContentWidthPt(pageWidth, marginX);
    const contentWidthPx = ptToCssPx(contentWidthPt);
    const contentHeight = pageHeight - contentTop - contentBottom;
    const maxContentHeightPx = Math.floor(ptToCssPx(contentHeight) * PAGE_HEIGHT_SAFETY_RATIO);

    const blocks = splitBodyHtmlBlocks(normalizeExportListHtml(bodyHtml));
    const pages = await paginateExportBlocks({
        blocks,
        title,
        includeTitle,
        maxContentHeightPx,
        contentWidthPx,
    });

    let logoDataUrl = null;
    try {
        logoDataUrl = await getLensLogoDataUrl();
    } catch {
        // Logo is best-effort; continue export without it.
        logoDataUrl = null;
    }

    let pageIndex = 0;

    for (const page of pages) {
        const html = buildNoteExportHtml({
            title,
            bodyHtml: page.blocks.join(''),
            includeTitle: page.includeTitle,
            contentWidthPx,
        });
        const host = createOffscreenExportRoot(html, contentWidthPx);

        try {
            const article = host.querySelector('.note-export') || host;
            await waitForImages(host);
            const canvas = await renderArticleToCanvas(article, contentWidthPx);
            pageIndex = addCanvasToPdf({
                pdf,
                canvas,
                pageIndex,
                contentWidthPt,
                contentTop,
                contentHeight,
                marginX,
                logoDataUrl,
                allowSlice: page.oversized,
            });
        } finally {
            host.remove();
        }
    }

    pdf.save(filename);
};

const textRunsFromNode = (node, marks = {}) => {
    if (!node) return [];

    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (!text) return [];
        return [{ text, ...marks }];
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return [];

    const tag = node.tagName.toLowerCase();
    if (tag === 'br') return [{ text: '\n', ...marks }];

    const nextMarks = { ...marks };
    if (tag === 'strong' || tag === 'b') nextMarks.bold = true;
    if (tag === 'em' || tag === 'i') nextMarks.italic = true;
    if (tag === 'u') nextMarks.underline = true;
    if (tag === 's' || tag === 'strike' || tag === 'del') nextMarks.strike = true;

    return Array.from(node.childNodes).flatMap(child => textRunsFromNode(child, nextMarks));
};

const runsToDocxXml = (runs, options = {}) => {
    const { fontSize = 22, bold = false, color = '1B2149' } = options;
    if (!runs.length) {
        return `<w:r><w:rPr><w:sz w:val="${fontSize}"/><w:szCs w:val="${fontSize}"/><w:color w:val="${color}"/></w:rPr><w:t xml:space="preserve"></w:t></w:r>`;
    }

    return runs
        .map(run => {
            const parts = String(run.text || '').split('\n');
            return parts
                .map((part, index) => {
                    const rPr = [
                        `<w:sz w:val="${fontSize}"/>`,
                        `<w:szCs w:val="${fontSize}"/>`,
                        `<w:color w:val="${color}"/>`,
                        run.bold || bold ? '<w:b/><w:bCs/>' : '',
                        run.italic ? '<w:i/><w:iCs/>' : '',
                        run.underline ? '<w:u w:val="single"/>' : '',
                        run.strike ? '<w:strike/>' : '',
                    ].join('');

                    const textXml = `<w:r><w:rPr>${rPr}</w:rPr><w:t xml:space="preserve">${escapeXml(part)}</w:t></w:r>`;
                    if (index < parts.length - 1) {
                        return `${textXml}<w:r><w:br/></w:r>`;
                    }
                    return textXml;
                })
                .join('');
        })
        .join('');
};

const paragraphXml = (runs, { style = null, fontSize = 22, bold = false, spacingAfter = 120 } = {}) => {
    const pPr = [
        style ? `<w:pStyle w:val="${style}"/>` : '',
        `<w:spacing w:after="${spacingAfter}"/>`,
    ].join('');

    return `<w:p><w:pPr>${pPr}</w:pPr>${runsToDocxXml(runs, { fontSize, bold })}</w:p>`;
};

const PT_TO_EMU = 12700;
const DOCX_LOGO_WIDTH_PT = 78;
const DOCX_LOGO_HEIGHT_PT = Math.round((DOCX_LOGO_WIDTH_PT * 128) / 352);

const dataUrlToUint8Array = dataUrl => {
    const base64 = String(dataUrl || '').split(',')[1];
    if (!base64) return null;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
};

/** Right-aligned Lens logo paragraph for DOCX header area. */
const lensLogoParagraphXml = (relationshipId = 'rIdImage1') => {
    const cx = DOCX_LOGO_WIDTH_PT * PT_TO_EMU;
    const cy = DOCX_LOGO_HEIGHT_PT * PT_TO_EMU;

    return `<w:p>
  <w:pPr>
    <w:jc w:val="right"/>
    <w:spacing w:after="200"/>
  </w:pPr>
  <w:r>
    <w:drawing>
      <wp:inline distT="0" distB="0" distL="0" distR="0">
        <wp:extent cx="${cx}" cy="${cy}"/>
        <wp:docPr id="1" name="Lens Logo"/>
        <a:graphic>
          <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">
            <pic:pic>
              <pic:nvPicPr>
                <pic:cNvPr id="0" name="Lens_Logo_Blue.png"/>
                <pic:cNvPicPr/>
              </pic:nvPicPr>
              <pic:blipFill>
                <a:blip r:embed="${relationshipId}"/>
                <a:stretch><a:fillRect/></a:stretch>
              </pic:blipFill>
              <pic:spPr>
                <a:xfrm>
                  <a:off x="0" y="0"/>
                  <a:ext cx="${cx}" cy="${cy}"/>
                </a:xfrm>
                <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
              </pic:spPr>
            </pic:pic>
          </a:graphicData>
        </a:graphic>
      </wp:inline>
    </w:drawing>
  </w:r>
</w:p>`;
};

const htmlToDocxParagraphs = (title, bodyHtml, includeTitle = true) => {
    const paragraphs = [];
    if (includeTitle) {
        paragraphs.push(
            paragraphXml([{ text: title || 'Untitled', bold: true }], {
                style: 'Heading1',
                fontSize: 36,
                bold: true,
                spacingAfter: 240,
            }),
        );
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${bodyHtml || ''}</div>`, 'text/html');
    const root = doc.body.firstElementChild || doc.body;

    const pushBlock = (el, defaults = {}) => {
        const tag = el.tagName.toLowerCase();
        const runs = textRunsFromNode(el);
        if (!runs.some(r => (r.text || '').trim()) && tag !== 'p') return;

        if (tag === 'h1') {
            paragraphs.push(paragraphXml(runs, { style: 'Heading1', fontSize: 32, bold: true, spacingAfter: 160 }));
            return;
        }
        if (tag === 'h2') {
            paragraphs.push(paragraphXml(runs, { style: 'Heading2', fontSize: 28, bold: true, spacingAfter: 140 }));
            return;
        }
        if (tag === 'h3' || tag === 'h4') {
            paragraphs.push(paragraphXml(runs, { style: 'Heading3', fontSize: 24, bold: true, spacingAfter: 120 }));
            return;
        }
        if (tag === 'li') {
            paragraphs.push(
                paragraphXml([{ text: `• ${runs.map(r => r.text).join('')}`, bold: defaults.bold }], {
                    fontSize: 22,
                    spacingAfter: 80,
                }),
            );
            return;
        }

        paragraphs.push(paragraphXml(runs.length ? runs : [{ text: '' }], { fontSize: 22, spacingAfter: 120, ...defaults }));
    };

    Array.from(root.childNodes).forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = (node.textContent || '').trim();
            if (text) paragraphs.push(paragraphXml([{ text }], { fontSize: 22 }));
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();
        if (tag === 'ul' || tag === 'ol') {
            Array.from(node.children).forEach(li => pushBlock(li));
            return;
        }
        if (tag === 'div') {
            Array.from(node.children).forEach(child => {
                if (child.nodeType === Node.ELEMENT_NODE) pushBlock(child);
            });
            if (!node.children.length) pushBlock(node);
            return;
        }
        pushBlock(node);
    });

    if (paragraphs.length === 0) {
        paragraphs.push(paragraphXml([{ text: '' }], { fontSize: 22 }));
    } else if (includeTitle && paragraphs.length === 1) {
        paragraphs.push(paragraphXml([{ text: '' }], { fontSize: 22 }));
    }

    return paragraphs.join('');
};

/** Build a DOCX blob from HTML (optionally with LENS logo header). */
export const createDocxBlobFromHtml = async ({ title = '', bodyHtml, includeLogo = true, includeTitle = true }) => {
    let logoBytes = null;
    if (includeLogo) {
        try {
            const logoDataUrl = await getLensLogoDataUrl();
            logoBytes = dataUrlToUint8Array(logoDataUrl);
        } catch {
            logoBytes = null;
        }
    }

    const logoParagraph = logoBytes ? lensLogoParagraphXml('rIdImage1') : '';

    const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
  xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"
  xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
  xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">
  <w:body>
    ${logoParagraph}
    ${htmlToDocxParagraphs(title, bodyHtml, includeTitle)}
    <w:sectPr>
      <w:pgSz w:w="12240" w:h="15840"/>
      <w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/>
    </w:sectPr>
  </w:body>
</w:document>`;

    const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Default Extension="png" ContentType="image/png"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`;

    const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;

    const documentRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${
      logoBytes
          ? `<Relationship Id="rIdImage1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/image1.png"/>`
          : ''
  }
</Relationships>`;

    const zip = new JSZip();
    zip.file('[Content_Types].xml', contentTypes);
    zip.folder('_rels').file('.rels', rels);
    const word = zip.folder('word');
    word.file('document.xml', documentXml);
    word.folder('_rels').file('document.xml.rels', documentRels);
    if (logoBytes) {
        word.folder('media').file('image1.png', logoBytes);
    }

    return zip.generateAsync({ type: 'blob' });
};

/** Export note as DOCX with headings/bold/italic/lists preserved (browser-safe, no Node polyfills). */
export const exportNoteAsDocx = async ({ title, bodyHtml }) => {
    const filename = `${sanitizeFilename(title)}.docx`;
    const blob = await createDocxBlobFromHtml({ title, bodyHtml, includeLogo: true });
    downloadBlob(blob, filename);
};
